import React, { useEffect, useRef, useState } from "react";
import { TriangleAlert } from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";
import { Drawer } from "@/design-system/components/Drawer/Drawer";
import { Typography } from "@/design-system/components/Typography/Typography";
import { sessionHasProgress } from "./navigation";
import { AnyWizardDefinition, WizardSession } from "./types";
import { WizardDock, WizardDockFocusRequest } from "./WizardDock";
import { WizardErrorBoundary } from "./WizardErrorBoundary";
import { WizardPanel } from "./WizardPanel";
import { useMinimizedWizards, useWizards } from "./WizardProvider";
import { WizardResetConfirmation } from "./WizardResetConfirmation";

const MINIMIZED_ANNOUNCEMENT =
  "Wizard minimized. Resume it from Minimized wizards.";

export function WizardHost() {
  const { definitions, state } = useWizards();
  const minimized = useMinimizedWizards();
  const [focusRequest, setFocusRequest] =
    useState<WizardDockFocusRequest | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const previousExpandedId = useRef(state.expandedId);

  const expandedSession = state.expandedId
    ? state.sessions[state.expandedId]
    : undefined;
  const expandedDefinition = expandedSession
    ? definitions[expandedSession.type]
    : undefined;

  useEffect(() => {
    const previous = previousExpandedId.current;
    previousExpandedId.current = state.expandedId;
    if (state.expandedId) {
      setAnnouncement("");
    } else if (previous && state.sessions[previous]) {
      setAnnouncement(MINIMIZED_ANNOUNCEMENT);
      setFocusRequest({ sessionId: previous });
    }
  }, [state.expandedId, state.sessions]);

  return (
    <>
      {expandedSession && expandedDefinition && (
        // Keyed by session so each session's hooks and step state start fresh.
        <WizardErrorBoundary
          key={expandedSession.id}
          wizardType={expandedDefinition.type}
          fallback={() => (
            <WizardCrashed
              definition={expandedDefinition}
              session={expandedSession}
            />
          )}
        >
          <WizardPanel
            definition={expandedDefinition}
            session={expandedSession}
          />
        </WizardErrorBoundary>
      )}
      <WizardErrorBoundary wizardType="dock" fallback={() => null}>
        <WizardDock wizards={minimized} focusRequest={focusRequest} />
      </WizardErrorBoundary>
      <div role="status" className="sr-only">
        {announcement}
      </div>
    </>
  );
}

function WizardCrashed({
  definition,
  session,
}: {
  definition: AnyWizardDefinition;
  session: WizardSession;
}) {
  const { minimizeWizard, discardWizard } = useWizards();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const requestDiscard = () => {
    if (sessionHasProgress(definition, session)) {
      setConfirmingDiscard(true);
    } else {
      discardWizard(session.id);
    }
  };

  return (
    <>
      <Drawer
        title={definition.title}
        open
        placement="bottom"
        onOpenChange={(open) => {
          if (!open) minimizeWizard();
        }}
        onCloseButtonClick={requestDiscard}
        closeButtonLabel="Discard wizard"
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
          <TriangleAlert
            className="text-status-warning size-icon-xl"
            strokeWidth={1.5}
            aria-hidden
          />
          <Typography variant="heading-md" as="h2">
            This wizard couldn’t open
          </Typography>
          <Typography variant="body-md" color="subtle">
            Minimize it to try again later, or discard it.
          </Typography>
          <div className="flex gap-2">
            <Button variant="outline" onClick={requestDiscard}>
              Discard
            </Button>
            <Button onClick={minimizeWizard}>Minimize</Button>
          </div>
        </div>
      </Drawer>
      <WizardResetConfirmation
        pending={confirmingDiscard ? "discard" : null}
        onCancel={() => setConfirmingDiscard(false)}
        onConfirm={() => {
          setConfirmingDiscard(false);
          discardWizard(session.id);
        }}
      />
    </>
  );
}
